import { createSelector } from 'reselect';
import { rootSelector } from '../selectors';

function notEmpty<T>(value: T | null): value is T {
  return value !== null && value !== undefined;
}

/**
 * Returns the InvoiceResponse with all of invoice header information,
 * current, and past invoices.
 */
export const getInvoiceResponse = createSelector(
  rootSelector,
  settings => settings.billing.invoices,
);

/**
 * Returns past invoices from the InvoiceResponse.
 */
export const getPastInvoices = createSelector(getInvoiceResponse, invoiceData =>
  (invoiceData?.invoices || []).filter(notEmpty),
);

export const getPaymentInfo = createSelector(
  rootSelector,
  settings => settings.billing.paymentInfo.data,
);

/**
 * undefined when data isn't loaded, null if a card doesn't exist, otherwise a CardInfo object
 */
export const getPaymentCard = createSelector(getPaymentInfo, paymentInfo => {
  if (!paymentInfo) {
    // data is not loaded yet
    return undefined;
  }

  const { cardInfo } = paymentInfo;

  // in practice this should always come back with an object.
  // when there is no card on file, all properties will be their default server value,
  // hence the check for a value on lastFour to determine if a card truly exists.
  return cardInfo && !!cardInfo.lastFour ? cardInfo : null;
});

export const getPaymentInvoiceEmail = createSelector(
  getPaymentInfo,
  paymentInfo => paymentInfo?.invoiceEmail ?? null,
);

export const getPaymentEntityId = createSelector(
  getPaymentInfo,
  paymentInfo => paymentInfo?.entityId ?? null,
);

export const getStripePublishableToken = createSelector(
  getPaymentInfo,
  paymentInfo => paymentInfo?.stripePublishableToken ?? null,
);
